"use client"

/**
 * AnimateText
 *
 * The single, canonical wrapper for animating **static text** (the metajive
 * line-reveal: GSAP SplitText `type:"lines"` + `mask:"lines"`, masked rotate-up
 * with a per-line stagger, triggered on scroll-in, plus an sr-only copy for a11y).
 *
 * Reach for this anytime you render static text you want revealed:
 *
 * ```tsx
 * <AnimateText element="h2">{headline}</AnimateText>
 * <AnimateText element="p">{eyebrow}</AnimateText>
 * ```
 *
 * Two paths across the codebase:
 * - **Static JSX text** → `<AnimateText element="…">{text}</AnimateText>` (this component).
 * - **CMS html string** → `<HtmlRenderer animate html={…} element="…" />`. For a CMS
 *   string that must sit inside a heading element, wrap explicitly:
 *   `<AnimateText element="h2"><HtmlRenderer html={x} /></AnimateText>` (AnimateText owns
 *   the heading, HtmlRenderer injects inline content — avoids `<div>`-in-`<h2>`).
 *   Never pass a raw html string as `children` — it would render escaped tags.
 *
 * Props:
 * - `element`: tag/component to render (default 'div').
 * - `variant`: 'lines' (default) | 'words' | 'chars'.
 * - `children`: the text/JSX to reveal.
 * - `delay`, `stagger`: animation timing (seconds).
 * - `className`, `id`, and `data-*` are forwarded to the rendered element so
 *   callers keep styling and a11y hooks (e.g. aria-labelledby targets).
 * - `sharedInViewId`: shared IntersectionObserver id (default 'animateText').
 *
 * Accessibility:
 * - Renders the visible (animated) copy `aria-hidden` plus an sr-only copy for
 *   screen readers; honors `prefers-reduced-motion` (text appears immediately).
 *
 * See also: useSharedInView, ScrollProvider, https://greensock.com/gsap/
 */

import type { ComponentPropsWithRef, ElementType, ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import cn from "classnames"
import { gsap } from "gsap"
import { SplitText } from "gsap/SplitText"

import { useScroll } from "~/components/foundations/ScrollProvider"
import { useIsomorphicLayoutEffect } from "~/hooks/useIsomorphicLayoutEffect"
import { useMatchMedia } from "~/hooks/useMatchMedia"
import { type ObserverId, useSharedInView } from "~/hooks/useSharedInView"

import s from "./AnimateText.module.scss"

gsap.registerPlugin(SplitText)

/**
 * Insert a `<wbr>` (zero-width line-break opportunity) right after every intra-word hyphen
 * in `root`'s text nodes, so a hyphenated compound (e.g. "employee-owned") can wrap at the
 * hyphen during the animation the same way it does at rest.
 */
const insertHyphenBreaks = (root: HTMLElement) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const targets: Text[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeValue && /\w-\w/.test(node.nodeValue))
      targets.push(node as Text)
  }
  for (const textNode of targets) {
    // Each segment ends right after an intra-word hyphen; a <wbr> goes between segments.
    // No lookbehind (Safari compatibility).
    const segments = textNode.nodeValue?.match(/.*?\w-(?=\w)|.+$/g) ?? []
    if (segments.length < 2) continue
    const frag = document.createDocumentFragment()
    segments.forEach((segment, i) => {
      frag.append(document.createTextNode(segment))
      if (i < segments.length - 1) frag.append(document.createElement("wbr"))
    })
    textNode.replaceWith(frag)
  }
}

type SplitTextVariant = keyof Pick<SplitText, "lines" | "words" | "chars">

type Props<TElement extends ElementType = "div"> = {
  element?: TElement
  variant?: SplitTextVariant
  children: ReactNode
  delay?: number
  sharedInViewId?: ObserverId
  className?: string
  stagger?: number
  /** Forwarded to the rendered element so callers keep id / data-* hooks
   * (e.g. aria-labelledby targets, styling attributes). */
  id?: string
  [dataAttr: `data-${string}`]: unknown
} & ComponentPropsWithRef<TElement>

export const AnimateText = <TElement extends ElementType = "div">({
  element = "div",
  variant = "lines",
  children,
  delay = 0,
  stagger = 0.02,
  sharedInViewId = "animateText",
  className,
  ...rest
}: Props<TElement>) => {
  const Container = element

  const { lenis } = useScroll()
  const [inViewId, setInViewId] = useState<ObserverId>("default")

  const { ref: contentRef, inView } = useSharedInView<HTMLDivElement>(
    inViewId,
    {
      once: true,
    },
  )

  const splitRef = useRef<SplitText | null>(null)

  const prefersReducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)")

  // Add hyphen break opportunities before the split runs, so the resting and split layouts
  // wrap hyphenated words identically (and revert restores the same DOM). Not gated by
  // `inView`: the resting state must be correct on first paint.
  useIsomorphicLayoutEffect(() => {
    if (contentRef.current) insertHyphenBreaks(contentRef.current)
  }, [contentRef, children])

  useEffect(() => {
    if (!lenis) return

    const handleScroll = () => {
      setInViewId(sharedInViewId)
      lenis.off("scroll", handleScroll)
    }
    lenis.on("scroll", handleScroll)

    return () => {
      lenis.off("scroll", handleScroll)
    }
  }, [lenis, sharedInViewId])

  useEffect(() => {
    const element = contentRef.current
    if (!element) return
    if (!inView) return
    if (prefersReducedMotion) return

    let cancelled = false
    const splitVariant: SplitTextVariant = variant

    // Keep the split layout as the final state (no revert on complete) so line breaks never
    // snap to a different natural layout — zero jump. SplitText groups lines using inline-block
    // word boxes that lose this heading's negative letter-spacing across word boundaries and so
    // wrap wider than the resting flow; for `lines` we expose the computed tracking to the
    // `.maskedLine` class, which re-applies it as `word-spacing` to pull each box back in during
    // measurement so the frozen breaks ≈ design, plus `reduceWhiteSpace:false` to keep inter-word
    // spaces. The only reason to unwind the split is a resize: revert once to plain text so it
    // reflows naturally (no replay).
    const compensateLetterSpacing = splitVariant === "lines"

    const handleResize = () => {
      splitRef.current?.revert()
      splitRef.current = null
      element.style.removeProperty("--aw-letter-spacing")
      window.removeEventListener("resize", handleResize)
    }

    document.fonts.ready.then(() => {
      if (cancelled || !contentRef.current) return

      if (compensateLetterSpacing) {
        const ls = getComputedStyle(contentRef.current).letterSpacing
        contentRef.current.style.setProperty(
          "--aw-letter-spacing",
          ls === "normal" ? "0px" : ls,
        )
      }

      splitRef.current = SplitText.create(contentRef.current, {
        type: splitVariant,
        mask: splitVariant,
        reduceWhiteSpace: false,
        linesClass: compensateLetterSpacing ? s.maskedLine : undefined,
        onSplit(self) {
          // The `mask: "lines"` wrappers use overflow:clip sized to the line box, which clips descenders under a tight line-height.
          if (compensateLetterSpacing) {
            for (const line of self.lines) {
              if (line.parentElement)
                line.parentElement.style.marginBottom = "-0.2em"
            }
          }
          return gsap.from(self[splitVariant], {
            delay,
            duration: 0.6,
            ease: "circ.out",
            stagger,
            yPercent: 100,
          })
        },
      })

      window.addEventListener("resize", handleResize)
    })

    return () => {
      cancelled = true
      splitRef.current?.revert()
      splitRef.current = null
      element.style.removeProperty("--aw-letter-spacing")
      window.removeEventListener("resize", handleResize)
    }
  }, [inView, contentRef, delay, variant, stagger, prefersReducedMotion])

  return (
    <>
      <Container
        {...rest}
        aria-hidden="true"
        ref={contentRef}
        className={cn(s.animatedTextContainer, className)}
      >
        {children}
      </Container>
      <Container className={s.srOnly}>{children}</Container>
    </>
  )
}
